import React, { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import axios from 'axios';
import AcademyHeader from '../../components/AcademyHeader';
import AcademyFooter from '../../components/AcademyFooter';
import ProgramIcon from '../../components/academy/ProgramIcon';
import SeoHead from '../../components/SeoHead';
import CrossDivisionRail from '../../components/CrossDivisionRail';
import { PROGRAM_BY_SLUG, PROGRAMS } from '../../data/programsContent';
import { getCourseSchema } from '../../data/courseSchema';
import { getBreadcrumbSchema } from '../../data/breadcrumbSchema';
import { ArrowLeft, ArrowRight, Check, Clock, GraduationCap, Users2 } from 'lucide-react';

const API = (process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL);

export default function ProgramDetail() {
  const { slug } = useParams();
  const staticProgram = PROGRAM_BY_SLUG[slug];
  const [customProgram, setCustomProgram] = useState(null);
  const [loaded, setLoaded] = useState(!!staticProgram);

  useEffect(() => {
    if (staticProgram) return; // no API call needed
    let active = true;
    axios.get(`${API}/api/academy/programs/${slug}`).then(({ data }) => {
      if (!active) return;
      // Adapt the lightweight admin-created program to the rich shape this page expects
      setCustomProgram({
        slug: data.slug,
        title: data.name,
        tagline: data.tagline || data.summary || '',
        icon: 'BookOpen',
        gradient: 'from-stone-600 to-stone-800',
        image: data.image_url || null,
        duration: data.duration || 'Self-paced',
        level: data.level || 'All levels',
        audience: data.audience || 'Aspiring learners',
        summary: data.description || data.summary || '',
        whatYouLearn: [],
        outcomes: [],
        syllabus: [],
        isCustom: true,
      });
    }).catch(() => {/* leaves customProgram null → redirect */}).finally(() => {
      if (active) setLoaded(true);
    });
    return () => { active = false; };
  }, [slug, staticProgram]);

  const p = staticProgram || customProgram;
  if (!loaded) {
    return (
      <div className="min-h-screen bg-white">
        <AcademyHeader />
        <div className="max-w-[900px] mx-auto px-6 py-20 text-[#6E6E73]">Loading program…</div>
      </div>
    );
  }
  if (!p) return <Navigate to="/academy/programs" replace />;

  // schema.org Course payload — surfaces this program in Google's "Courses"
  // SERP carousel. Provider links back to the canonical Organization node.
  // Plus a BreadcrumbList so Google replaces URL crumbs with named ones.
  const courseJsonLd = [
    getCourseSchema(p),
    getBreadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Academy', path: '/academy' },
      { name: 'Programs', path: '/academy/programs' },
      { name: p.title, path: `/academy/programs/${p.slug}` },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title={`${p.title} · ZHELTA Academy`}
        description={p.summary}
        path={`/academy/programs/${p.slug}`}
        jsonLd={courseJsonLd}
      />
      <AcademyHeader />

      {/* Hero */}
      <section className={`relative overflow-hidden bg-gradient-to-br ${p.gradient} text-white`}>
        {p.image && (
          <>
            <img
              src={p.image}
              alt={p.title}
              className="absolute inset-0 w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
          </>
        )}
        <div className="relative max-w-[1200px] mx-auto px-4 lg:px-6 py-14 lg:py-20">
          <Link to="/academy/programs" data-testid="program-detail-back" className="inline-flex items-center gap-1.5 text-[12px] text-white/80 hover:text-white mb-5">
            <ArrowLeft className="w-3.5 h-3.5" /> All programs
          </Link>
          <div className="flex items-start gap-5">
            <div className="hidden sm:flex w-16 h-16 rounded-2xl bg-white/15 backdrop-blur items-center justify-center shrink-0">
              <ProgramIcon name={p.icon} className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] tracking-[0.26em] uppercase font-bold text-white/70 mb-2">ZHELTA Academy</div>
              <h1 className="text-[34px] sm:text-[44px] lg:text-[52px] font-extrabold leading-[1.05] mb-3">{p.title}</h1>
              <p className="text-[15px] lg:text-[17px] text-white/85 max-w-[720px]">{p.tagline}</p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Pill><Clock className="w-3.5 h-3.5" /> {p.duration}</Pill>
                <Pill><Users2 className="w-3.5 h-3.5" /> {p.level}</Pill>
                <Pill><GraduationCap className="w-3.5 h-3.5" /> {p.audience}</Pill>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1200px] mx-auto px-4 lg:px-6 py-14 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-10">
          <div>
            <h2 className="text-[22px] font-extrabold text-[#1D1D1F] mb-2">Overview</h2>
            <p className="text-[15px] text-[#3a3a3f] leading-relaxed">{p.summary}</p>
          </div>

          <div>
            <h2 className="text-[22px] font-extrabold text-[#1D1D1F] mb-3">What you'll learn</h2>
            {p.whatYouLearn.length === 0 ? (
              <p className="text-[14px] text-[#6E6E73]">Curriculum is being finalised. Enrol now to get notified when it goes live.</p>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {p.whatYouLearn.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-[14px] text-[#3a3a3f]">
                    <Check className="w-4 h-4 text-[#c9a227] mt-0.5 shrink-0" /> {item}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {p.outcomes.length > 0 && (
            <div>
              <h2 className="text-[22px] font-extrabold text-[#1D1D1F] mb-3">Outcomes</h2>
              <ul className="space-y-2">
                {p.outcomes.map((o) => (
                  <li key={o} className="flex items-start gap-2 text-[14px] text-[#3a3a3f]">
                    <ArrowRight className="w-4 h-4 text-[#c9a227] mt-0.5 shrink-0" /> {o}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {p.syllabus.length > 0 && (
            <div>
              <h2 className="text-[22px] font-extrabold text-[#1D1D1F] mb-3">Syllabus</h2>
              <ol className="space-y-2.5">
                {p.syllabus.map((s) => (
                  <li key={s.title} className="rounded-2xl border border-[#E5E5E7] p-4">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <h3 className="text-[15px] font-bold text-[#1D1D1F]">{s.title}</h3>
                      <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#a8871a] bg-amber-50 border border-amber-100 px-2 py-0.5 rounded-full">Week {s.week}</span>
                    </div>
                    <p className="text-[13.5px] text-[#6E6E73]">{s.body}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        {/* Enroll CTA */}
        <aside className="lg:sticky lg:top-6 h-fit rounded-2xl border border-[#E5E5E7] bg-white text-zinc-900 p-6 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <div className="text-[11px] tracking-[0.24em] uppercase font-bold text-[#c9a227] mb-2">Ready to enroll?</div>
          <h3 className="text-[20px] font-extrabold mb-1 text-zinc-900">Pick a tier that fits.</h3>
          <p className="text-[13.5px] text-zinc-500 mb-5">Tap any tier to request enrollment — the form opens with that path pre-selected.</p>
          <ul className="space-y-2.5 mb-6">
            <Tier program={p} name="Standard"     desc="Self-paced + mentor Q&A" />
            <Tier program={p} name="Premium"      desc="Cohort + 1:1 mentoring + capstone" />
            <Tier program={p} name="Career Cohort" desc="12 weeks + career coaching + job support" />
          </ul>
          {/* Fallback CTA for visitors who haven't decided on a tier yet.
              Sends to the same form but WITHOUT a path param so all 3
              learning paths stay visible. */}
          <Link
            to={`/academy/enroll?program=${encodeURIComponent(p.slug)}`}
            data-testid="program-detail-request-enroll"
            className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 rounded-full text-[13px] font-bold uppercase tracking-[0.18em] text-[#0e0e10] hover:brightness-95 transition-all"
            style={{
              background: 'linear-gradient(90deg, #ffb347 0%, #f5a623 50%, #ff8c1a 100%)',
              boxShadow: '0 10px 20px -8px rgba(245, 140, 25, 0.45)',
            }}
          >
            Not sure? Get a recommendation <ArrowRight className="w-4 h-4" />
          </Link>
          <p className="mt-3 text-[11.5px] text-zinc-400 leading-snug">
            Your academy admin will email a personal enrollment link with payment details.
          </p>
        </aside>
      </section>

      {/* Related programs */}
      <section className="bg-[#FAFAFA] border-t border-[#E5E5E7]">
        <div className="max-w-[1200px] mx-auto px-4 lg:px-6 py-12">
          <h2 className="text-[18px] font-extrabold text-[#1D1D1F] mb-4">Explore other programs</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {PROGRAMS.filter((x) => x.slug !== p.slug).slice(0, 4).map((x) => (
              <Link key={x.slug} to={`/academy/programs/${x.slug}`} className="block rounded-xl border border-[#E5E5E7] bg-white p-4 hover:border-[#c9a227] transition-colors">
                <div className="mb-2"><ProgramIcon name={x.icon} className="w-6 h-6 text-[#c9a227]" /></div>
                <div className="text-[13.5px] font-bold text-[#1D1D1F] leading-snug">{x.title}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CrossDivisionRail slug={p.slug} kind="academy_program" />

      <AcademyFooter />
    </div>
  );
}

function Pill({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 border border-white/20 text-[11px] font-semibold">
      {children}
    </span>
  );
}

function Tier({ program, name, desc }) {
  // Each tier now deep-links to the enrollment request form with the
  // program + learning path pre-selected AND locked (user only sees the
  // tier they chose). Top-nav "ENROLL NOW" or the "Not sure?" CTA below
  // go to the form without a path param so all 3 tiers are visible.
  return (
    <li>
      <Link
        to={`/academy/enroll?program=${encodeURIComponent(program.slug)}&path=${encodeURIComponent(TIER_SLUG[name] || 'standard')}`}
        data-testid={`academy-tier-${name.toLowerCase().replace(/\s+/g, '-')}`}
        className="group flex items-start gap-3 rounded-lg bg-white hover:bg-amber-50/40 border border-zinc-200 hover:border-[#c9a227] px-3 py-2.5 transition-colors"
      >
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-extrabold flex items-center gap-1.5 text-zinc-900">
            {name}
          </div>
          <div className="text-[11.5px] text-zinc-500">{desc}</div>
        </div>
        <ArrowRight
          className="w-4 h-4 text-zinc-400 shrink-0 mt-0.5 group-hover:text-[#c9a227] group-hover:translate-x-0.5 transition-all"
          aria-hidden="true"
        />
      </Link>
    </li>
  );
}

// Name → `learning_path` id expected by `/academy/enroll?path=` + backend.
const TIER_SLUG = {
  'Standard':      'standard',
  'Premium':       'premium',
  'Career Cohort': 'career_cohort',
};



